import React, { useState } from 'react';
import { Copy, Eye, EyeOff, KeyRound, Loader2, RefreshCw, X } from 'lucide-react';
import { api } from '../context/AuthContext';

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!#%+';
export const generatePassword = (len = 14) => {
  const arr = new Uint32Array(len);
  window.crypto.getRandomValues(arr);
  let pw = Array.from(arr, (n) => ALPHABET[n % ALPHABET.length]).join('');
  if (!/\d/.test(pw)) pw = `${pw.slice(0, -1)}7`;
  if (!/[A-Za-z]/.test(pw)) pw = `K${pw.slice(1)}`;
  return pw;
};

export const PasswordDialog = ({ user, onClose, onDone, isSelf = false }) => {
  const [pw, setPw] = useState(generatePassword());
  const [show, setShow] = useState(true);
  const [mustChange, setMustChange] = useState(!isSelf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const valid = pw.length >= 8 && /\d/.test(pw) && /[A-Za-z]/.test(pw);

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.post(`/admin/users/${user.user_id}/password`, { password: pw, must_change: mustChange });
      setDone(true);
      onDone?.();
    } catch (e) { setError(e?.response?.data?.detail || 'Passwort konnte nicht gesetzt werden'); }
    finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(pw);

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" data-testid="password-dialog" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-sm shadow-xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-[#7a8a99] hover:text-[#1B3A5C]" aria-label="Schließen" data-testid="password-dialog-close"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-2 text-[11px] tracking-[0.28em] text-[#b8893d] uppercase mb-2"><KeyRound className="w-4 h-4" /> Passwort setzen</div>
        <h3 className="font-serif text-xl text-[#1B3A5C]">{user.name || user.email}</h3>
        <p className="text-xs text-[#7a8a99] mb-4">{user.email}{user.auth_provider === 'google' ? ' · Google-Login (Passwort-Login wird zusätzlich aktiviert)' : ''}</p>
        {done ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-sm text-emerald-900" data-testid="password-dialog-success">
            <div className="font-medium mb-1">Passwort gespeichert.</div>
            <div className="text-xs">Bitte dem Nutzer sicher übermitteln (nicht per unverschlüsselter E-Mail an Dritte). {isSelf ? '' : 'Bestehende Anmeldungen wurden abgemeldet.'}</div>
            <div className="mt-3 flex items-center gap-2 font-mono text-sm bg-white border border-emerald-200 px-3 py-2 rounded-sm" data-testid="password-dialog-final"><span className="flex-1 break-all">{pw}</span><button type="button" onClick={copy} className="text-[#1B3A5C]" title="Kopieren"><Copy className="w-4 h-4" /></button></div>
            <button type="button" onClick={onClose} className="mt-4 text-xs tracking-wider px-4 py-2 bg-[#1B3A5C] text-white rounded-sm" data-testid="password-dialog-finish">FERTIG</button>
          </div>
        ) : (
          <>
            <label className="block text-[10px] tracking-wider text-[#7a8a99] uppercase mb-1">Neues Passwort (min. 8 Zeichen, Buchstaben + Ziffern)</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-3 py-2.5 pr-10 border border-gray-200 focus:border-[#b8893d] focus:outline-none text-sm rounded-sm font-mono" data-testid="password-dialog-input" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-2 top-2.5 text-[#7a8a99]" aria-label="Anzeigen" data-testid="password-dialog-toggle">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <button type="button" onClick={() => setPw(generatePassword())} className="px-3 border border-gray-200 rounded-sm text-[#1B3A5C] hover:border-[#b8893d]" title="Neu generieren" data-testid="password-dialog-generate"><RefreshCw className="w-4 h-4" /></button>
              <button type="button" onClick={copy} className="px-3 border border-gray-200 rounded-sm text-[#1B3A5C] hover:border-[#b8893d]" title="Kopieren" data-testid="password-dialog-copy"><Copy className="w-4 h-4" /></button>
            </div>
            {!valid && pw && <p className="text-xs text-rose-600 mt-1">Mindestens 8 Zeichen mit Buchstaben und Ziffern.</p>}
            {!isSelf && (
              <label className="flex items-center gap-2 text-xs text-[#34536b] mt-3"><input type="checkbox" checked={mustChange} onChange={(e) => setMustChange(e.target.checked)} className="accent-[#b8893d]" data-testid="password-dialog-mustchange" /> Nutzer muss das Passwort beim nächsten Login ändern</label>
            )}
            {error && <p className="text-xs text-rose-600 mt-2" data-testid="password-dialog-error">{error}</p>}
            <div className="mt-5 flex gap-2 justify-end">
              <button type="button" onClick={onClose} className="text-xs tracking-wider px-4 py-2 border border-gray-200 rounded-sm">ABBRECHEN</button>
              <button type="button" onClick={save} disabled={busy || !valid} className="inline-flex items-center gap-2 text-xs tracking-wider px-4 py-2 bg-[#1B3A5C] text-white rounded-sm disabled:opacity-60" data-testid="password-dialog-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} PASSWORT SPEICHERN</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
